import React, { useState, useEffect } from 'react';
import { Leadership, LeadershipImage } from '../../types/Leadership';
import { LeadershipService } from '../../services/LeadershipService';
import { Upload, X } from 'lucide-react';

export interface LeadershipFormData {
  name: string;
  title: string;
  biography: string;
  quote?: string;
  image?: LeadershipImage;
  isActive: boolean;
}

interface LeadershipFormProps {
  leadership?: Leadership | null;
  onSubmit: (data: LeadershipFormData) => Promise<Leadership>; // Changed to return Leadership
  onCancel: () => void;
}

const LeadershipForm: React.FC<LeadershipFormProps> = ({ leadership, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<LeadershipFormData>({
    name: '',
    title: '',
    biography: '',
    quote: '',
    isActive: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Use the same simple image URL logic as working choir images
  const getImageUrl = (image: any): string => {
    if (image?.s3Url) return image.s3Url;
    if (image?.s3Key && image.s3Key.startsWith('data:')) return image.s3Key;
    return '';
  };

  useEffect(() => {
    if (leadership) {
      setFormData({
        name: leadership.name,
        title: leadership.title,
        biography: leadership.biography,
        quote: leadership.quote || '',
        image: leadership.image,
        isActive: leadership.isActive,
      });

      if (leadership.image) {
        const imageUrl = getImageUrl(leadership.image);
        if (imageUrl) {
          setImagePreview(imageUrl);
          console.log('Setting image preview from URL:', imageUrl);
        } else {
          console.log('No valid image URL found for leadership:', leadership.image);
        }
      }
    }
  }, [leadership]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev: LeadershipFormData) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = async () => {
    if (leadership?.image) {
      try {
        await LeadershipService.deleteLeadershipImage(leadership.id);
        setFormData((prev: LeadershipFormData) => ({ ...prev, image: undefined }));
      } catch (error) {
        console.error('Error removing image:', error);
      }
    }
    setImageFile(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Submit the form data first
      const submittedLeadership = await onSubmit(formData);

      // If there's a new image file, upload it using the returned leadership ID
      if (imageFile) {
        await LeadershipService.uploadLeadershipImage(submittedLeadership.id, imageFile);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Image Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Profile Image
        </label>

        {/* Image Guidelines */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <h4 className="text-sm font-medium text-blue-800 mb-2">📸 Standardized Image Guidelines:</h4>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• <strong>Aspect Ratio:</strong> 3:2 (landscape) - REQUIRED for consistency</li>
            <li>• <strong>Recommended Size:</strong> 1200x800px for best quality</li>
            <li>• <strong>Minimum Size:</strong> 600x400px</li>
            <li>• <strong>Format:</strong> JPG or PNG files only</li>
            <li>• <strong>File Size:</strong> Under 2MB</li>
            <li>• <strong>Examples:</strong> Professional headshots cropped to 3:2 ratio work best</li>
            <li>• <strong>Tips:</strong> Use photo editing software to crop to exact 3:2 ratio before uploading</li>
          </ul>
        </div>

        <div className="flex items-center space-x-4">
          {(imagePreview || formData.image) && (
            <div className="relative">
              <img
                src={imagePreview || (() => {
                  if (!formData.image) return '';
                  // Get image URL - use S3 URL if available, fallback to base64 for legacy images
                  if (formData.image.s3Url) {
                    return formData.image.s3Url;
                  }
                  // Fallback for legacy base64 images (stored in s3Key field for backward compatibility)
                  if (formData.image.s3Key && formData.image.s3Key.startsWith('data:')) {
                    return formData.image.s3Key;
                  }
                  return '';
                })()}
                alt="Preview"
                className="w-24 h-24 object-cover rounded-lg border border-gray-300"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X size={12} />
              </button>
            </div>
          )}
          <input
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleImageChange}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-full file:border-0
              file:text-sm file:font-semibold
              file:bg-blue-50 file:text-blue-700
              hover:file:bg-blue-100"
          />
        </div>
      </div>

      {/* Name */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          required
          value={formData.name}
          onChange={handleInputChange}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700">
          Title *
        </label>
        <input
          type="text"
          id="title"
          name="title"
          required
          value={formData.title}
          onChange={handleInputChange}
          placeholder="e.g., Director of Choral Activities, Music Teacher"
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Biography */}
      <div>
        <label htmlFor="biography" className="block text-sm font-medium text-gray-700">
          Biography *
        </label>
        <textarea
          id="biography"
          name="biography"
          required
          rows={4}
          value={formData.biography}
          onChange={handleInputChange}
          placeholder="Brief biography and background..."
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Quote */}
      <div>
        <label htmlFor="quote" className="block text-sm font-medium text-gray-700">
          Quote (Optional)
        </label>
        <input
          type="text"
          id="quote"
          name="quote"
          value={formData.quote}
          onChange={handleInputChange}
          placeholder="An inspiring quote or motto..."
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      {/* Active Status */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isActive"
          name="isActive"
          checked={formData.isActive}
          onChange={handleInputChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
          Active (visible on public pages)
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : leadership ? 'Update Leadership' : 'Create Leadership'}
        </button>
      </div>
    </form>
  );
};

export default LeadershipForm;
